import type { ContentType, LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import type { Field } from 'api/contentful/utilTypes';
import type { HTMLAttributes } from 'react';

/* flattening utils */

export type FlatSysV2 = Partial<{ id: string; contentType: string }>;
export type HasContentType = { contentType: ContentType };

export type EntryV2<T> = FlatSysV2 & T;

export type EntriesV2<T> = ReadonlyArray<Field<T>>;

/* Contentful State types */

export enum Status {
  IDLE = 'idle',
  PENDING = 'pending',
  RESOLVED = 'resolved',
  ERRORED = 'errored',
}

export enum CmsActionTypes {
  FETCH = 'fetch',
  SUCCESS = 'success',
  ERROR = 'error',
}

export type CmsActions<T> =
  | {
      type: CmsActionTypes.FETCH;
    }
  | {
      type: CmsActionTypes.SUCCESS;
      payload: T;
    }
  | {
      type: CmsActionTypes.ERROR;
      payload?: string;
    };

export type CmsState<T> = {
  data?: T;
  status: Status;
  message?: string;
};

/**
 * Helper types for common generic contentful entries.
 * These types are static and potentially transformed to meet this app
 * contract. These are not supposed to be 1:1 with Contentful itself as
 * these should be CMS agnostic and provide the intended data
 * specifically for the type's purpose.
 */

export declare namespace AppType {
  /**
   * Top level type only used with components or modules that
   * the CMS may directly invoke. Even then, this should only
   * be used if needing access to the _attributes property to
   * access debuging attributes or styles.
   *
   * {@link ICmsComponentAttributes}
   */
  type Component<T> = Content<
    T & {
      _attributes?: ICmsComponentAttributes;
      headingIndex?: number;
    }
  >;

  /**
   * A base type used to reference an application-level type
   * contract with common generic types (eg Link, Image, etc.)
   *
   * @example AppType.Content<Page>
   */
  type Content<T = { [key: string]: any }> = {
    [P in keyof T]?: T[P] extends object ? Content<T[P]> : T[P];
  };
}

export type Asset = AppType.Content<{
  id?: string;
  title: string;
  description: string;
  file: File;
}>;
export type Image = Asset;

export type Link = AppType.Content<{
  title: string;
  url: string;
  isExternal: boolean;
  newTab: boolean;
  variant: LinkVariant;
  queryParameters:
    | any[]
    | {
        [key: string]: any;
      };
  disallowedResellers: string[];
  image: Image;
}>;

type File = AppType.Content<{
  url: string;
  fileName: string;
  details: {
    size: number;
    image?: {
      width: number;
      height: number;
    };
  };
}>;

/**
 * Exposes the internal CMS-related properties to the type.
 *
 * @example WithCMS<Link> will provide access to _sys
 */
export type WithCMS<T = {}, E = ICmsSys> = E & {
  [P in keyof T]?: T[P] extends AppType.Content | undefined ? WithCMS<T[P], Partial<ICmsSys>> : T[P];
};

/**
 * Used to provide internal CMS-related properties. Most
 * components will not need to use this.
 */
export interface ICmsSys {
  _sys: ICmsComponentSys;
}

/**
 * Exposes the contentful's contentType
 * used on existing components like: ContentLinkV2
 * @param content decoupled cms content
 * @returns an typed EntryV2 with contentType
 */
export function toEntry(sys: unknown) {
  const content = sys as ICmsSys;
  return {
    contentType: content?._sys?.contentType,
    ...content,
  } as EntryV2<WithCMS>;
}

/**
 * Exposes the contentful's sys id
 * used on FlatEntry
 * @param content decoupled cms content
 * @returns sys id
 */
export function toSysId(content: unknown) {
  return (content as ICmsSys)._sys?.id;
}

/**
 * The CMS-related system properties related to the entry
 */
interface ICmsComponentSys extends FlatSysV2 {
  internalName?: string;
}

/**
 * Component attributes that may be added along the render
 * chain to provide additional debug or style information for
 * the component. {@link AppType.Component}
 */
interface ICmsComponentAttributes extends HTMLAttributes<HTMLElement> {
  'data-content-type'?: string;
  'data-entry-id'?: string;
  'data-field'?: string;
}
